/**
 * Persistent auth storage for Capacitor (iOS/Android).
 * Mirrors Supabase session to native storage so users stay logged in across app restarts,
 * like other native apps. On web, this is a no-op (localStorage is already persistent).
 * Uses static import for @capacitor/preferences so the plugin resolves in the WebView.
 */

import { Preferences } from '@capacitor/preferences';
import { authLog, authWarn } from './authDiagnostics';
import { isCapacitor } from './platform';

const AUTH_KEY = 'trackio-auth-token';
/** Mirrors AuthContext / localStorage: survives cold start when WebView localStorage is empty. */
const RETURNING_VISITOR_PREFS_KEY = 'trackio_has_logged_in_before';
/** Same suffix GoTrue uses: `${storageKey}-code-verifier` */
const CODE_VERIFIER_LOCAL_KEY = `${AUTH_KEY}-code-verifier`;
/** Native mirror so PKCE survives iOS WebView reload after ASWebAuthenticationSession (localStorage is often empty on the next document load). */
const PKCE_VERIFIER_PREFS_KEY = 'trackio-pkce-code-verifier';

async function persistPkceVerifierToNative(value) {
  if (!isCapacitor()) return;
  try {
    await Preferences.set({ key: PKCE_VERIFIER_PREFS_KEY, value });
  } catch (e) {
    console.warn('[authStorage] Persist PKCE verifier failed:', e?.message || e);
  }
}

async function clearPkceVerifierFromNative() {
  if (!isCapacitor()) return;
  try {
    await Preferences.remove({ key: PKCE_VERIFIER_PREFS_KEY });
  } catch (e) {
    console.warn('[authStorage] Clear PKCE verifier failed:', e?.message || e);
  }
}

async function mergeReturningVisitorFromNative() {
  if (!isCapacitor()) return;
  try {
    const { value } = await Preferences.get({ key: RETURNING_VISITOR_PREFS_KEY });
    if (value === '1' && typeof localStorage !== 'undefined') {
      localStorage.setItem(RETURNING_VISITOR_PREFS_KEY, '1');
      authLog('hydrate', 'restored returning-visitor flag from Preferences');
    }
  } catch (e) {
    authWarn('hydrate', 'returning-visitor Preferences.get failed', { message: e?.message });
  }
}

/** Call after a successful login so returning-visitor state survives even if WebView storage was cleared. */
export async function persistReturningVisitorToNative() {
  if (!isCapacitor()) return;
  try {
    await Preferences.set({ key: RETURNING_VISITOR_PREFS_KEY, value: '1' });
  } catch (e) {
    authWarn('persistReturningVisitor', 'Preferences.set failed', { message: e?.message });
  }
}

/**
 * On app cold start (or when app becomes active): restore session from native storage into localStorage.
 * Call this before the app bootstraps (e.g. in main.jsx before first render) or when app becomes active.
 * @returns {Promise<{ restored: boolean, initialUser?: { email: string, businessName: string, role: string } }>}
 *   restored: true if a value was written to localStorage; initialUser: minimal user from token (for cold start).
 */
export async function hydrateAuthFromNativeStorage() {
  if (!isCapacitor()) {
    authLog('hydrate', 'skip (not Capacitor)');
    return { restored: false };
  }

  try {
    const { value: pkce } = await Preferences.get({ key: PKCE_VERIFIER_PREFS_KEY });
    if (pkce && typeof pkce === 'string' && typeof localStorage !== 'undefined') {
      localStorage.setItem(CODE_VERIFIER_LOCAL_KEY, pkce);
      authLog('hydrate', 'restored PKCE verifier from Preferences', { length: pkce.length });
    }
  } catch (e) {
    authWarn('hydrate', 'PKCE Preferences.get failed', { message: e?.message });
  }

  authLog('hydrate', 'Preferences.get start');
  try {
    const { value } = await Preferences.get({ key: AUTH_KEY });
    authLog('hydrate', 'Preferences.get done', { hasValue: !!(value && typeof value === 'string'), valueLength: typeof value === 'string' ? value.length : 0 });
    if (value && typeof value === 'string') {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(AUTH_KEY, value);
      }
      try {
        const parsed = JSON.parse(value);
        const token = parsed?.access_token;
        authLog('hydrate', 'parse session', { hasToken: !!token });
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const email = payload?.email ?? payload?.user_email;
          authLog('hydrate', 'JWT payload', { hasEmail: !!email, email: email || null });
          if (email) {
            await mergeReturningVisitorFromNative();
            return { restored: true, initialUser: { email, businessName: '', role: 'user' } };
          }
        }
      } catch (parseErr) {
        authLog('hydrate', 'JWT parse failed', { message: parseErr?.message });
      }
      await mergeReturningVisitorFromNative();
      return { restored: true };
    }
    authLog('hydrate', 'no value in Preferences');
  } catch (e) {
    authWarn('hydrate', 'Preferences.get failed', { message: e?.message });
  }

  await mergeReturningVisitorFromNative();
  return { restored: false };
}

/**
 * After auth changes: mirror current session from localStorage to native storage.
 * Call from onAuthStateChange when session exists, or after login/session restore.
 */
export async function persistAuthToNativeStorage() {
  if (!isCapacitor()) return;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_KEY) : null;
    if (raw) {
      await Preferences.set({ key: AUTH_KEY, value: raw });
    } else {
      // No session in localStorage (e.g. user logged out) — clear Preferences so they stay logged out on reopen
      await Preferences.remove({ key: AUTH_KEY });
    }
  } catch (e) {
    console.warn('[authStorage] Persist to native storage failed:', e?.message || e);
  }
}

/**
 * On logout: clear session from native storage.
 */
export async function clearAuthFromNativeStorage() {
  if (!isCapacitor()) return;
  try {
    await Preferences.remove({ key: AUTH_KEY });
    await Preferences.remove({ key: PKCE_VERIFIER_PREFS_KEY });
    try {
      if (typeof localStorage !== 'undefined') localStorage.removeItem(CODE_VERIFIER_LOCAL_KEY);
    } catch (_) {}
  } catch (e) {
    console.warn('[authStorage] Clear native storage failed:', e?.message || e);
  }
}

/**
 * Custom storage adapter for Supabase when in Capacitor.
 * - getItem/setItem: use localStorage and mirror to Preferences on set.
 * - removeItem: NO-OP for the session key (keep stale-session UX). For `*-code-verifier`, clear local + native
 *   so PKCE cleanup after exchange matches GoTrue.
 * So when Supabase "clears" the session (e.g. after failed token refresh on cold start),
 * we keep the session in localStorage so getInitialUserFromToken() and the app stay logged in.
 * Only explicit logout (clearAuthFromNativeStorage + localStorage.removeItem in AuthContext) clears the session.
 */
export function getCapacitorAuthStorage() {
  if (!isCapacitor()) return null;
  return {
    getItem(key) {
      try {
        return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        // When Supabase clears session (e.g. refresh_token_not_found), it passes "". Don't overwrite.
        // Keeps user logged in so they see the app and can re-login gracefully instead of blank screen.
        if (!value) return;
        if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
        // PKCE verifier is written before any session exists — do not run persistAuthToNativeStorage here
        // (it would Preferences.remove(AUTH_KEY) and is unnecessary for verifier-only writes).
        if (key.endsWith('-code-verifier')) {
          persistPkceVerifierToNative(value).catch(() => {});
          return;
        }
        persistAuthToNativeStorage().catch(() => {});
      } catch (_) {}
    },
    removeItem(key) {
      if (!key || !key.endsWith('-code-verifier')) return;
      try {
        if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
      } catch (_) {}
      clearPkceVerifierFromNative().catch(() => {});
    }
  };
}
